import { useEffect, useState } from 'react';
import { message, Button, notification, Modal } from 'antd';
import { GiftOutlined, TrophyOutlined } from '@ant-design/icons';
import { UPDATE_USER_RECOGNITION_EVENTS } from 'src/_shared/api/graphql/custom/recognition/recognitionMutations';
import { motion } from 'motion/react';
import ConfettiExplosion from 'react-confetti-explosion';
import MyrewardsPresentation from '../../presentation/myrewardsPresentation';
import useRewardDetails from '../../../RnRCommonComponent/RewardList/hooks/useRewardDetails';
import { useSelector } from 'react-redux';
import { snappy } from 'src/_shared';
import { useApolloClient, useMutation } from '@apollo/client';
import SnappyGiftService from '../../service/SnappyGift.service.jsx';
import GiftImage from 'src/_shared/assets/snappy_present.png';
import dompurify from 'dompurify';
import uuid from 'uuid/v4';

const sanitizer = dompurify.sanitize;

const confettiConfig = {
	force: 0.9,
	duration: 3000,
	particleCount: 300,
	width: '2000',
	height: 1600,
	colors: ['#f650a9', '#00c3ff', '#fffc00', '#00ff87', '#ff0055', '#7700ff'],
};

const MyrewardsContainer = ({ milestone }) => {
	const [campaigns, setCampaigns] = useState([]);
	const currentUser = useSelector((state) => state.user.currentUser);
	const customModal = currentUser?.company?.recognitionSettings?.customModal;
	const [isViewRewardModalVisible, setIsViewRewardModalVisible] =
		useState(false);
	const [testRecognition, setTestRecognition] = useState(null);
	const [disableSpotlight, setDisableSpotlight] = useState(false);
	const [recentActivities, setRecentActivities] = useState([]);
	const [isLoadingActivities, setIsLoadingActivities] = useState(true);
	const [badgeAssignments, setBadgeAssignments] = useState([]);
	const [snappyGifts, setSnappyGifts] = useState([]);
	const [badgeGiftsWithStatus, setBadgeGiftsWithStatus] = useState([]);
	const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);

	const [showGiftModal, setShowGiftModal] = useState(false);
	const [isExploding, setIsExploding] = useState(false);
	const [claimedGiftData, setClaimedGiftData] = useState(null);
	const [processingGiftClaims, setProcessingGiftClaims] = useState(new Set());
	const [processingMilestoneClaims, setProcessingMilestoneClaims] = useState(
		new Set()
	);

	const apolloClient = useApolloClient();
	const snappyGiftService = new SnappyGiftService(apolloClient);

	const [updateUserRecognitionEvents] = useMutation(
		UPDATE_USER_RECOGNITION_EVENTS,
		{
			variables: {
				input: {
					id: currentUser.id,
					recognitionEvents: currentUser?.recognitionEvents,
				},
			},
		}
	);

	const handleHasSeenPopup = async () => {
		await updateUserRecognitionEvents({
			variables: {
				input: {
					id: currentUser.id,
					recognitionEvents: {
						...currentUser.recognitionEvents,
						hasSeenPopup: true,
					},
				},
			},
		});
	};

	const handleCustomModalClose = () => {
		setIsCustomModalVisible(false);
		localStorage.setItem('hasSeenRecognitionPopup', 'true');

		if (!currentUser?.recognitionEvents?.hasSeenPopup) {
			handleHasSeenPopup();
		}
	};

	const {
		rewardDetails,
		isModalLoaderVisible,
		handleOpenViewReward: handleOpenViewRewardGraphQL,
	} = useRewardDetails(setIsViewRewardModalVisible, currentUser, apolloClient);

	useEffect(() => {
		const loadInitialData = async () => {
			setIsLoadingActivities(true);
			try {
				const badgeGiftsWithStatusData = await fetchBadgeGiftsWithStatus();

				const activities = snappyGiftService.processRecentActivities(
					badgeGiftsWithStatusData
				);
				setRecentActivities(activities);
			} finally {
				setIsLoadingActivities(false);
			}
		};

		loadInitialData();
	}, []);

	useEffect(() => {
		if (
			!isLoadingActivities &&
			(badgeAssignments.length > 0 ||
				snappyGifts.length > 0 ||
				badgeGiftsWithStatus.length > 0)
		) {
			updateRecentActivities();
		}
	}, [badgeAssignments, snappyGifts, badgeGiftsWithStatus]);

	useEffect(() => {
		const hasSeenRecognitionPopup = localStorage.getItem(
			'hasSeenRecognitionPopup'
		);

		if (
			(currentUser?.company?.recognitionSettings?.disableRecHub === true,
			!hasSeenRecognitionPopup &&
				customModal?.isEnabled === true &&
				(customModal?.title || customModal?.content) &&
				(!currentUser?.recognitionEvents?.hasSeenPopup ||
					customModal?.showEachLogin === true))
		) {
			setIsCustomModalVisible(true);
		}
	}, [customModal, currentUser?.recognitionEvents?.hasSeenPopup]);

	const fetchBadgeGiftsWithStatus = async () => {
		try {
			const gifts = await snappyGiftService.querySnappyGiftsByUserIdCompanyId(
				currentUser.id,
				currentUser.company.id
			);

			const badgeGifts = gifts.filter(
				(gift) =>
					gift.badgeAssignmentId &&
					gift.externalSnappyGiftId &&
					gift.externalSnappyGiftId !== 'undefined'
			);

			const giftsWithStatus = await Promise.all(
				badgeGifts.map(async (gift) => {
					const statusData = await snappyGiftService.getSnappyGift(
						currentUser.id,
						gift.externalSnappyGiftId
					);
					return {
						...gift,
						statusData,
					};
				})
			);
			setBadgeGiftsWithStatus(giftsWithStatus);
			return giftsWithStatus;
		} catch (error) {
			console.error('Error fetching badge gifts with status:', error);
			return [];
		}
	};

	const updateRecentActivities = () => {
		const activities =
			snappyGiftService.processRecentActivities(badgeGiftsWithStatus);
		setRecentActivities(activities);
	};

	const handleTierClaim = async (tier) => {
		if (tier?.id && tier?.snappyGrantId) {
			setProcessingGiftClaims((prev) => new Set([...prev, tier.id]));
			try {
				const response = await snappy({
					type: 'createGiftsFromNewTiers',
					userId: currentUser.id,
					sendingMethod: 'link',
					recipients: [currentUser.id],
					campaignTierId: tier.id,
				});

				if (response && response.length > 0) {
					const result = response[0];
					const giftId = result.id || result.giftId;
					const giftLink = result.link || result.giftDetails?.link;

					if (giftId && giftLink) {
						setClaimedGiftData({
							id: giftId,
							snappyUrl: giftLink,
							title: 'Your Reward is Ready!',
							tierName: tier.name,
						});

						setShowGiftModal(true);
						setIsExploding(true);
					} else {
						message.error(result.errorMessage || 'Error claiming gift.');
						console.error('Error claiming gift:', result.errorMessage);
					}
				} else {
					message.error('Unexpected response from the server.');
					console.error('Unexpected response:', response);
				}
			} catch (error) {
				console.error('Error claiming gift', error);
				message.error('An error occurred while claiming the gift.');
			} finally {
				setProcessingGiftClaims((prev) => {
					const newSet = new Set(prev);
					newSet.delete(tier.id);
					return newSet;
				});
			}
		}
	};

	const createSnappyGiftForRecipient = async (activity) => {
		setProcessingGiftClaims(
			(prev) => new Set([...prev, activity.badgeAssignmentId])
		);
		try {
			const snappyGiftInput = {
				id: uuid(),
				companyId: activity.companyId,
				userId: currentUser.id,
				badgeAssignmentId: activity.badgeAssignmentId,
				snappyGrantId: activity.snappyGrantId,
				externalSnappyGiftId: activity.externalSnappyGiftId,
				claimedOn: new Date().toISOString(),
			};

			const snappyGiftResponse = await snappy({
				type: 'directCreateGift',
				sendingMethod: 'link',
				snappyCampaignId: activity.externalSnappyGiftId,
				snappyGiftInput: snappyGiftInput,
			});
			if (snappyGiftResponse?.link) {
				setClaimedGiftData({
					id: activity.id,
					snappyUrl: snappyGiftResponse.link,
					title: 'Your Reward is Ready!',
					badgeName: activity.badgeName,
				});
				setShowGiftModal(true);
				setIsExploding(true);
			} else {
				message.error('Error claiming gift');
				console.error('Error claiming gift');
			}
		} catch (error) {
			console.error('Error creating snappy gift:', error);
		} finally {
			setProcessingGiftClaims((prev) => {
				const newSet = new Set(prev);
				newSet.delete(activity.badgeAssignmentId);
				return newSet;
			});
		}
	};

	const handleClaimGift = async (activity) => {
		try {
			if (activity.type === 'tier_claim' && activity.tier) {
				return await handleTierClaim(activity.tier);
			}

			if (
				activity.type === 'gift_uncreated' &&
				activity.snappyGrantId &&
				activity.externalSnappyGiftId
			) {
				return await createSnappyGiftForRecipient(activity);
			}

			// Handle non-tier cards (badge-based gifts) with new logic
			if (activity.type === 'gift_received' && activity.giftStatus) {
				if (activity.giftStatus === 'unopened' && activity.link) {
					// For unopened gifts, just show confetti modal with the link
					setClaimedGiftData({
						id: activity.id,
						snappyUrl: activity.link,
						title: 'Your Reward is Ready!',
						badgeName: activity.badgeName,
					});

					setShowGiftModal(true);
					setIsExploding(true);
					return;
				}

				if (
					activity.giftStatus === 'unwrapped' ||
					activity.giftStatus === 'opened'
				) {
					// For unwrapped gifts, the button should show "Finish Claiming Reward"
					// but we still need to handle the claim action
					if (activity.link) {
						window.open(activity.link, '_blank');
						return;
					}
				}
			}

			const snappyGrantId = activity.snappyGrantId;

			if (!snappyGrantId) {
				message.error('Unable to claim reward. Missing snappy grant ID.');
				return;
			}

			if (snappyGrantId.startsWith('test-')) {
				const mockClaimedGift = {
					id: activity.id,
					externalSnappyGiftId: 'test-snappy-123',
					createdAt: new Date().toISOString(),
					title: 'Test Gift Claimed',
				};

				setClaimedGiftData(mockClaimedGift);
				setShowGiftModal(true);
				setIsExploding(true);

				notification.destroy();

				setRecentActivities((prev) => prev.filter((a) => a.id !== activity.id));

				return;
			}

			// Find the badge assignment that matches this snappyGrantId
			const matchingBadgeAssignment = badgeAssignments.find(
				(assignment) => assignment.badge?.snappyGrantId === snappyGrantId
			);

			if (!matchingBadgeAssignment) {
				message.error(
					`Unable to find badge assignment for snappyGrantId: ${snappyGrantId}`
				);
				return;
			}

			if (!matchingBadgeAssignment.badge?.snappyGrant?.snappyCampaignTierId) {
				message.error(
					'Badge assignment missing snappyCampaignTierId information.'
				);
				return;
			}

			await snappyGiftService.markGiftAsSeen(
				currentUser.id,
				currentUser.company.id,
				snappyGrantId
			);

			await fetchBadgeGiftsWithStatus();
		} catch (error) {
			console.error('Error claiming gift:', error);
			message.error('Failed to process gift. Please try again.');

			await fetchBadgeGiftsWithStatus();
		}
	};

	const openSnappyLink = (link) =>
		window.open(link, '_blank', 'noopener,noreferrer');

	const runMilestoneClaim = async (key, action) => {
		setProcessingMilestoneClaims((prev) => new Set([...prev, key]));
		try {
			await action();
		} catch (error) {
			console.error('Error claiming milestone reward:', error);
			message.error('Something went wrong. Please try again.');
		} finally {
			setProcessingMilestoneClaims((prev) => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		}
	};

	const handleClaimMilestonePin = async (claim) => {
		if (claim.pin.link) {
			openSnappyLink(claim.pin.link);
			return;
		}

		await runMilestoneClaim(`${claim.id}:pin`, async () => {
			const link = await milestone.claimPin(claim);
			if (link) {
				openSnappyLink(link);
			} else {
				message.success('Your pin has been claimed!');
			}
		});
	};

	const handleClaimMilestoneGift = async (claim) => {
		// Open synchronously when the link already exists so the browser doesn't block it
		if (claim.gift.link) {
			openSnappyLink(claim.gift.link);
			milestone.markGiftStarted(claim);
			return;
		}

		await runMilestoneClaim(`${claim.id}:gift`, async () => {
			const link = await milestone.claimGift(claim);
			if (link) {
				openSnappyLink(link);
			} else {
				message.error('Error claiming gift');
			}
		});
	};

	const handleMarkAsSeen = async (activity) => {
		try {
			setRecentActivities((prev) =>
				prev.map((a) =>
					a.id === activity.id ? { ...a, hasSeen: true, isNew: false } : a
				)
			);
		} catch (error) {
			console.error('Error marking activity as seen:', error);
		}
	};

	const handleViewRecognition = (activity) => {
		if (activity.rawData?.badgeId) {
			handleOpenViewReward(activity.rawData.badgeId);
		}
	};

	const handleGiftModalClose = () => {
		setShowGiftModal(false);
		setIsExploding(false);
		setClaimedGiftData(null);
	};

	const handleGiftRedeem = () => {
		if (claimedGiftData?.snappyUrl) {
			window.open(claimedGiftData.snappyUrl, '_blank');
		} else if (claimedGiftData?.externalSnappyGiftId) {
			const snappyUrl = `https://snappy.com/gift/${claimedGiftData.externalSnappyGiftId}`;
			window.open(snappyUrl, '_blank');
		} else {
			window.open('https://snappy.com', '_blank');
		}
		handleGiftModalClose();
	};

	const handleViewBadgeDetails = (badgeAssignment) => {
		const badgeId = badgeAssignment.badgeId || badgeAssignment?.badge?.id;
		if (badgeId) {
			handleOpenViewRewardGraphQL(
				badgeId,
				badgeAssignment.completedAt,
				badgeAssignment
			);
		}
	};
	const handleCloseViewReward = () => {
		setIsViewRewardModalVisible(false);
	};

	const createTestRecognition = () => {
		setDisableSpotlight(false);

		const testId = `test-${Date.now()}`;

		const newTestRecognition = {
			id: testId,
			badgeId: `test-badge-${Date.now()}`,
			title: 'Test Recognition',
			description: 'This is a test recognition to verify animations',
			status: 'unviewed',
			createdAt: new Date().toISOString(),
			giftDetails: {
				link: 'https://snappy.com',
			},
			points: 500,
			iconCode: 'trophy',
			badgeTypeCode: 'PEERTOPEER',
			color: '#E75DA7',
		};

		const testGiftActivity = {
			id: `test-gift-${Date.now()}`,
			type: 'gift_received',
			title: 'Test Gift Received',
			description: 'You received a test gift!',
			createdAt: new Date().toISOString(),
			isNew: true,
			shouldShake: true,
			shakeIndex: 0,
			snappyGrantId: 'test-snappy-grant-123',
			rawData: {
				campaignTierId: 'test-campaign-tier-456',
			},
		};

		setRecentActivities((prev) => [testGiftActivity, ...prev]);

		const recognitionId = newTestRecognition.id;
		setTestRecognition(newTestRecognition);

		const key = `notification-${recognitionId}`;
		notification.open({
			message: 'You received a recognition!',
			description:
				'Your Recognition included a gift! Click the icon to find out more.',
			icon: <TrophyOutlined style={{ color: '#E75DA7' }} />,
			duration: 0,
			key,
			placement: 'topRight',
			onClose: () => {
				setDisableSpotlight(true);
			},
			style: {
				borderLeft: '4px solid #E75DA7',
				boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
			},
		});

		message.success(
			'Test recognition created! Scroll down to see it with spotlight effect.'
		);
	};

	const handleClearTestRecognition = () => {
		setTestRecognition(null);
		setDisableSpotlight(true);
	};

	return (
		<>
			{currentUser?.featureFlags?.includes('test_snappy_spotlight') ? (
				<div style={{ marginBottom: '20px', textAlign: 'center' }}>
					<Button
						type="primary"
						onClick={createTestRecognition}
						size="large"
						style={{
							background: '#E75DA7',
							borderColor: '#E75DA7',
							boxShadow: '0 4px 12px rgba(231, 93, 167, 0.3)',
						}}
					>
						Create Test Gift with Button Spotlight
					</Button>
				</div>
			) : null}
			<MyrewardsPresentation
				tiers={
					campaigns.length > 0 && campaigns[0]?.tiers ? campaigns[0].tiers : []
				}
				handleOpenViewReward={handleOpenViewRewardGraphQL}
				handleCloseViewReward={handleCloseViewReward}
				isViewRewardModalVisible={isViewRewardModalVisible}
				rewardDetails={rewardDetails}
				isModalLoaderVisible={isModalLoaderVisible}
				currentUser={currentUser}
				recentActivities={recentActivities}
				badgeAssignments={badgeAssignments}
				onClaimGift={handleClaimGift}
				testRecognition={testRecognition}
				disableSpotlight={disableSpotlight}
				onViewRecognition={handleViewRecognition}
				onViewBadgeDetails={handleViewBadgeDetails}
				onMarkAsSeen={handleMarkAsSeen}
				onClearTestRecognition={handleClearTestRecognition}
				processingGiftClaims={processingGiftClaims}
				isLoadingActivities={isLoadingActivities}
				milestoneClaims={milestone?.claims || []}
				processingMilestoneClaims={processingMilestoneClaims}
				onClaimMilestonePin={handleClaimMilestonePin}
				onClaimMilestoneGift={handleClaimMilestoneGift}
			/>

			{/* Custom Recognition Popup */}
			<Modal
				width={420}
				open={isCustomModalVisible}
				title={customModal?.title || 'Recognition'}
				footer={null}
				onCancel={handleCustomModalClose}
				destroyOnClose={true}
				centered
			>
				<div
					dangerouslySetInnerHTML={{
						__html: sanitizer(customModal?.content || '', {
							ADD_ATTR: ['target', 'rel'],
						}),
					}}
				/>
			</Modal>

			{/* Gift Claiming Confetti Modal */}
			<Modal
				title="Congratulations!"
				open={showGiftModal}
				onCancel={handleGiftModalClose}
				footer={[
					<Button key="close" onClick={handleGiftModalClose}>
						Close
					</Button>,
					<Button
						key="redeem"
						type="primary"
						onClick={handleGiftRedeem}
						size="large"
					>
						Go to Snappy and unwrap it!
					</Button>,
				]}
				className="recognition-reward-modal"
				width={800}
				centered
			>
				{isExploding && (
					<div
						style={{
							position: 'absolute',
							overflow: 'visible',
							top: '25%',
							left: '50%',
						}}
					>
						<ConfettiExplosion {...confettiConfig} />
					</div>
				)}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.2 }}
					className="recognition-modal-content"
				>
					<motion.h2
						initial={{ scale: 0.8, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{
							duration: 0.8,
							delay: 0.4,
							type: 'spring',
							stiffness: 100,
						}}
						style={{ marginTop: 12 }}
					>
						Your Gift has been claimed successfully!
					</motion.h2>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.6 }}
					>
						<img src={GiftImage} alt="Art of wrapped gift" />
					</motion.div>
				</motion.div>
			</Modal>
		</>
	);
};

export default MyrewardsContainer;
